/**
 * lib/leavePolicy.ts
 * ------------------------------------------------------------------
 * HR LEAVE — cohort entitlement policies (pure).
 *
 * Leave-day entitlements are configured per LEAVE YEAR and per COHORT,
 * where a cohort is the staff category (teaching / admin / support /
 * management / ancillary). The effective entitlement for a staff
 * member resolves, per leave type, by precedence:
 *
 *     per-staff allocation override  >  cohort policy
 *       >  category default  >  system default
 *
 * Policy docs live in `hrLeavePolicy`, id = `${year}_${category}`.
 * Balances stay fully derived — this layer only changes which
 * entitlement numbers feed computeLeaveBalances; the taken/remaining
 * arithmetic is unchanged.
 * ------------------------------------------------------------------ */
import type { StaffCategory } from '@/lib/hr';
import {
  DEFAULT_LEAVE_ENTITLEMENTS,
  DEFAULT_ENTITLEMENTS_BY_CATEGORY,
  LEAVE_ACCRUAL_START_BY_CATEGORY,
  type HrLeaveAllocation,
  type LeaveBalance,
} from '@/lib/hrPayroll';
import { computeLeaveBalances, type LeaveCalcOptions, type LeaveRequestLike, type EncashmentLike } from '@/lib/hrLeave';

export const LEAVE_POLICY_COLLECTION = 'hrLeavePolicy';

/** Leave types that carry an annual day entitlement (unpaid is excluded). */
export const ENTITLEMENT_LEAVE_TYPES = [
  'annual', 'sick', 'hospitalisation', 'maternity', 'paternity',
  'compassionate', 'study',
] as const;

export interface HrLeavePolicy {
  id?: string; // `${year}_${category}`
  year: string;
  category: StaffCategory;
  /** Per-leave-type day entitlement for this cohort/year. */
  entitlements: Record<string, number>;
  createdAt?: string;
  updatedAt?: string;
}

export function leavePolicyKey(year: string, category: StaffCategory): string {
  return `${year}_${category}`;
}

export function findPolicy(
  policies: HrLeavePolicy[],
  year: string,
  category?: StaffCategory,
): HrLeavePolicy | undefined {
  if (!category) return undefined;
  return policies.find((p) => p.year === year && p.category === category);
}

/**
 * Resolve effective per-type entitlements for one staff member.
 * Per-type merge: system default → cohort policy → per-staff override.
 */
export function resolveEntitlements(
  category: StaffCategory | undefined,
  allocation: HrLeaveAllocation | undefined,
  policies: HrLeavePolicy[],
  year: string,
): Record<string, number> {
  const cohort = findPolicy(policies, year, category)?.entitlements ?? {};
  const staff = allocation?.entitlements ?? {};
  // The category default sits between the school-wide one and the cohort
  // policy, so a school that has not written a teaching policy still gets
  // the 21 days teachers actually have rather than the school-wide figure.
  const byCategory = (category && DEFAULT_ENTITLEMENTS_BY_CATEGORY[category]) || {};
  return { ...DEFAULT_LEAVE_ENTITLEMENTS, ...byCategory, ...cohort, ...staff };
}

/**
 * Compute leave balances honouring the cohort policy. Reuses the pure
 * computeLeaveBalances by feeding it the resolved entitlements (and the
 * staff member's own opening balances), so taken/remaining stay derived.
 */
export function leaveBalancesFor(args: {
  category?: StaffCategory;
  allocation?: HrLeaveAllocation;
  requests: LeaveRequestLike[];
  policies: HrLeavePolicy[];
  year: string;
  alwaysShow?: string[];
  /** Date the opening balances are "as of"; annual accrues from here. */
  accrualStart?: string;
  /** Value the balance at this point in time (default: now). */
  asOf?: Date;
  /** Employee start date (for staff hired after go-live). */
  employmentStart?: string;
  /** Approved leave sales — see EncashmentLike. */
  encashments?: EncashmentLike[];
}): LeaveBalance[] {
  const basis = leaveBasis(args);
  return computeLeaveBalances(basis.allocation, args.requests, {
    ...basis.opts,
    alwaysShow: args.alwaysShow ?? ['annual', 'sick', 'hospitalisation'],
    asOf: args.asOf,
    encashments: args.encashments,
  });
}

/**
 * What a balance for one member of staff is worked out FROM: the resolved
 * entitlements (allocation over cohort policy over default) and the accrual
 * options (their cohort's anchor, their start date, the leave year).
 *
 * The one place that decides it, so the unpaid-leave check that docks pay
 * (hrWrite syncUnpaidLeaveDeduction) uses exactly the figures the screens
 * show. Before Oct 2026 it took the bare allocation and the June anchor:
 * a teacher's sick leave was 0 there and their accrual started three months
 * early (HIGH-RISK-CHANGES.md §14).
 */
export function leaveBasis(args: {
  category?: StaffCategory;
  allocation?: HrLeaveAllocation;
  policies: HrLeavePolicy[];
  year: string;
  accrualStart?: string;
  employmentStart?: string;
}): { allocation: HrLeaveAllocation; opts: LeaveCalcOptions } {
  const allocation: HrLeaveAllocation = {
    id: args.allocation?.id ?? '',
    staffId: args.allocation?.staffId ?? '',
    entitlements: resolveEntitlements(args.category, args.allocation, args.policies, args.year),
    openingBalances: args.allocation?.openingBalances,
    year: args.year,
    createdAt: args.allocation?.createdAt ?? '',
    updatedAt: args.allocation?.updatedAt ?? '',
  };
  /* Each cohort accrues from the date ITS opening balances were taken.
     Teachers were seeded as at 30 September 2026 and start accruing on 1
     October; accruing them from the non-teaching June anchor would credit
     three months they were never on this model for. */
  const accrualStart = args.accrualStart
    ?? (args.category ? LEAVE_ACCRUAL_START_BY_CATEGORY[args.category] : undefined);
  return {
    allocation,
    opts: { accrualStart, employmentStart: args.employmentStart, leaveYear: Number(args.year) || undefined, category: args.category },
  };
}

/** Default leave year as a string (calendar year). */
export function currentLeaveYear(now: Date = new Date()): string {
  return String(now.getFullYear());
}
